import { useCallback, useEffect, useRef } from "react";

import { getChat, getGroupChatMessages } from "../../api/message.api";
import { MessageRow } from "../../api/message.type";
import { useEventStreamingControls } from "../EventStreamingWrapper";
import { StreamCallbacks } from "../event-streaming/useEventStreamLifecycle";
import { SyncMessagesFn } from "./useMessageSync";

const MAX_CANONICAL_ATTEMPTS = 6;
const INITIAL_RETRY_DELAY_MS = 300;
const MAX_RETRY_DELAY_MS = 2000;

interface UseAssistantDraftsOptions {
  isGroupChat: boolean;
  groupChatId?: string;
  userId?: string;
  channelKey: string;
  registerStreamCallbacks: (
    channelKey: string,
    callbacks: StreamCallbacks | null
  ) => void;
  syncMessages: SyncMessagesFn;
  setIsTyping: (value: boolean) => void;
}

export function useAssistantDrafts({
  isGroupChat,
  groupChatId,
  userId,
  channelKey,
  registerStreamCallbacks,
  syncMessages,
  setIsTyping,
}: UseAssistantDraftsOptions) {
  const { setStreamedText } = useEventStreamingControls();
  const draftMessageMapRef = useRef<Map<string, string>>(new Map());
  const latestStreamTextRef = useRef<string>("");
  const pendingResolutionTimersRef = useRef<
    Map<string, ReturnType<typeof setTimeout>>
  >(new Map());

  const clearPendingResolution = useCallback((sourceMessageId: string) => {
    const timer = pendingResolutionTimersRef.current.get(sourceMessageId);
    if (timer) {
      clearTimeout(timer);
      pendingResolutionTimersRef.current.delete(sourceMessageId);
    }
  }, []);

  const createAssistantDraft = useCallback(
    (sourceMessageId: string) => {
      if (!sourceMessageId) return;

      const draftId = `draft-${sourceMessageId}`;
      draftMessageMapRef.current.set(sourceMessageId, draftId);

      const draft: MessageRow = {
        id: draftId,
        content: "",
        created_at: new Date().toISOString(),
        deleted_at: null,
        generated_by: "assistant",
        group_chat_id: isGroupChat ? groupChatId ?? null : null,
        agent_id: null,
        is_deleted: null,
        message_type: null,
        project_id: null,
        reply_to_message_id: sourceMessageId,
        response_id: null,
        role: "assistant",
        run_id: null,
        thread_id: null,
        user_id: userId ?? null,
        message_file_store: [],
        user: {},
      } as MessageRow;

      syncMessages((prev) => {
        const withoutDraft = prev.filter((msg) => msg.id !== draftId);
        return {
          next: [draft, ...withoutDraft],
        };
      });
    },
    [groupChatId, isGroupChat, syncMessages, userId]
  );

  const updateAssistantDraftContent = useCallback(
    (sourceMessageId: string, content: string) => {
      const draftId = draftMessageMapRef.current.get(sourceMessageId);
      if (!draftId) return;

      syncMessages((prev) => {
        const next = prev.map((msg) =>
          msg.id === draftId ? { ...msg, content } : msg
        );
        return { next };
      });
    },
    [syncMessages]
  );

  const cleanupAssistantDraft = useCallback(
    (sourceMessageId: string) => {
      const draftId = draftMessageMapRef.current.get(sourceMessageId);
      if (!draftId) return;

      draftMessageMapRef.current.delete(sourceMessageId);
      clearPendingResolution(sourceMessageId);
      syncMessages((prev) => {
        const next = prev.filter((msg) => msg.id !== draftId);
        return { next };
      });
    },
    [clearPendingResolution, syncMessages]
  );

  const fetchFinalMessage = useCallback(
    async (sourceMessageId: string): Promise<MessageRow | null> => {
      try {
        if (isGroupChat) {
          if (!groupChatId) {
            return null;
          }
          const { messages: latestMessages } = await getGroupChatMessages(
            groupChatId,
            1,
            5
          );
          return (
            latestMessages.find(
              (msg) =>
                msg.reply_to_message_id === sourceMessageId &&
                msg.role === "assistant"
            ) ?? null
          );
        }

        const { messages: latestMessages } = await getChat(1, 5);
        return (
          latestMessages.find(
            (msg) =>
              msg.reply_to_message_id === sourceMessageId &&
              msg.role === "assistant"
          ) ?? null
        );
      } catch (error) {
        console.warn("finalizeAssistantMessage_fetch_failed", error);
        return null;
      }
    },
    [groupChatId, isGroupChat]
  );

  const scheduleCanonicalResolution = useCallback(
    function resolver(sourceMessageId: string, attempt = 1) {
      const delay = Math.min(
        INITIAL_RETRY_DELAY_MS * attempt,
        MAX_RETRY_DELAY_MS
      );

      const existingTimer =
        pendingResolutionTimersRef.current.get(sourceMessageId);
      if (existingTimer) {
        clearTimeout(existingTimer);
      }

      const timer = setTimeout(async () => {
        pendingResolutionTimersRef.current.delete(sourceMessageId);

        const canonicalMessage = await fetchFinalMessage(sourceMessageId);

        if (canonicalMessage) {
          const draftId = draftMessageMapRef.current.get(sourceMessageId);
          draftMessageMapRef.current.delete(sourceMessageId);

          syncMessages((prev) => {
            const baseMessages = draftId
              ? prev.filter((msg) => msg.id !== draftId)
              : prev;

            const alreadyExists = baseMessages.some(
              (msg) => msg.id === canonicalMessage.id
            );

            if (alreadyExists) {
              const next = baseMessages.map((msg) =>
                msg.id === canonicalMessage.id ? canonicalMessage : msg
              );
              return { next };
            }

            return {
              next: [canonicalMessage, ...baseMessages],
              totalCountDelta: 1,
            };
          });

          setStreamedText("");
          latestStreamTextRef.current = "";
          return;
        }

        if (attempt >= MAX_CANONICAL_ATTEMPTS) {
          draftMessageMapRef.current.delete(sourceMessageId);
          return;
        }

        resolver(sourceMessageId, attempt + 1);
      }, delay);

      pendingResolutionTimersRef.current.set(sourceMessageId, timer);
    },
    [fetchFinalMessage, setStreamedText, syncMessages]
  );

  const finalizeAssistantMessage = useCallback(
    async (sourceMessageId: string, finalText?: string): Promise<boolean> => {
      setIsTyping(false);
      const draftId = draftMessageMapRef.current.get(sourceMessageId);

      if (finalText) {
        latestStreamTextRef.current = finalText;
      }

      const canonicalMessage =
        draftId != null ? await fetchFinalMessage(sourceMessageId) : null;

      if (canonicalMessage) {
        clearPendingResolution(sourceMessageId);
        draftMessageMapRef.current.delete(sourceMessageId);

        syncMessages((prev) => {
          const baseMessages = draftId
            ? prev.filter((msg) => msg.id !== draftId)
            : prev;

          const alreadyExists = baseMessages.some(
            (msg) => msg.id === canonicalMessage.id
          );

          if (alreadyExists) {
            const next = baseMessages.map((msg) =>
              msg.id === canonicalMessage.id ? canonicalMessage : msg
            );
            return { next };
          }

          return {
            next: [canonicalMessage, ...baseMessages],
            totalCountDelta: 1,
          };
        });

        latestStreamTextRef.current = "";
        setStreamedText("");
        return true;
      }

      if (draftId) {
        syncMessages((prev) => {
          const next = prev.filter((msg) => msg.id !== draftId);
          return { next };
        });
      }

      clearPendingResolution(sourceMessageId);
      scheduleCanonicalResolution(sourceMessageId);

      if (finalText) {
        setStreamedText((prev) => (prev ? prev : finalText));
      }

      return false;
    },
    [
      clearPendingResolution,
      fetchFinalMessage,
      scheduleCanonicalResolution,
      setIsTyping,
      setStreamedText,
      syncMessages,
    ]
  );

  useEffect(() => {
    registerStreamCallbacks(channelKey, {
      onStreamStart: (messageId) => {
        latestStreamTextRef.current = "";
        createAssistantDraft(messageId);
      },
      onTextDelta: ({ messageId, fullText }) => {
        latestStreamTextRef.current = fullText;
        updateAssistantDraftContent(messageId, fullText);
      },
      onStreamEnd: async ({ messageId, finalResponse }) => {
        const resolvedText = finalResponse ?? latestStreamTextRef.current;
        const resolvedImmediately = await finalizeAssistantMessage(
          messageId,
          resolvedText
        );
        return { preserveStreamText: !resolvedImmediately };
      },
      onError: ({ messageId, error }) => {
        console.warn("stream_error", error);
        cleanupAssistantDraft(messageId);
        setIsTyping(false);
        latestStreamTextRef.current = "";
      },
    });

    return () => {
      registerStreamCallbacks(channelKey, null);
    };
  }, [
    cleanupAssistantDraft,
    createAssistantDraft,
    finalizeAssistantMessage,
    channelKey,
    registerStreamCallbacks,
    setIsTyping,
    updateAssistantDraftContent,
  ]);

  useEffect(() => {
    return () => {
      pendingResolutionTimersRef.current.forEach((timer) => {
        clearTimeout(timer);
      });
      pendingResolutionTimersRef.current.clear();
    };
  }, []);
}
